import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppLoader from '@nxsys/components/AppLoader';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  useExportEarningStatement,
  useGetEarningStatementList,
} from '@nxsys/hooks/api/report/useReportApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { IEarningReportV2 } from '@nxsys/types/models/earningReport/v2';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getEarningStatementColumns } from './columns';
import EarningStatementFilterModal, {
  EarningStatementFilterValues,
} from './EarningStatementFilterModal';

const EarningStatementListing = () => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const currentTaxYear = getTaxWeekTaxYear().taxYear;
  const [searchText, setSearchText] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterValues, setFilterValues] =
    useState<EarningStatementFilterValues>({});
  const [exportLoadingId, setExportLoadingId] = useState<string | null>(null);
  const selectedTaxYearRef = useRef<number>(currentTaxYear);

  const {
    value: earningData,
    loading,
    run: fetchEarnings,
  } = useGetEarningStatementList();
  const { run: exportEarning, loading: isExportLoading } =
    useExportEarningStatement();

  const handleFetchData = useCallback(
    (params: {
      year?: string;
      taxPeriod?: number;
      sortField?: string;
      sortType?: 'asc' | 'desc';
    }) => {
      fetchEarnings({
        year: params.year ?? String(selectedTaxYearRef.current),
        taxPeriod: params.taxPeriod,
        sortField: params.sortField,
        sortType: params.sortType,
      });
    },
    [fetchEarnings],
  );

  // Fetch data on mount
  useEffect(() => {
    handleFetchData({ year: String(currentTaxYear) });
  }, [currentTaxYear]);

  const handleTaxYearChange = useCallback(
    (value: { taxYear: number | undefined }) => {
      const taxYear = value.taxYear ?? currentTaxYear;
      selectedTaxYearRef.current = taxYear;
      handleFetchData({
        year: String(taxYear),
        taxPeriod: filterValues.taxPeriod,
      });
    },
    [filterValues, handleFetchData, currentTaxYear],
  );

  const handleFilter = useCallback(
    (values: EarningStatementFilterValues) => {
      setFilterValues(values);
      handleFetchData({
        year: values.year
          ? String(values.year)
          : String(selectedTaxYearRef.current),
        taxPeriod: values.taxPeriod,
      });
    },
    [handleFetchData],
  );

  // Client-side search filtering
  const filteredList = useMemo(() => {
    const list = earningData?.data ?? [];
    if (!searchText) return list;
    const lower = searchText.toLowerCase();
    return list.filter((item) => {
      const fullName = item.subcontractorRef?.fullName?.toLowerCase() ?? '';
      const niNumber = item.subcontractorRef?.NINumber?.toLowerCase() ?? '';
      const utr =
        item.subcontractorRef?.setting?.UTRNo?.toLowerCase() ?? '';
      const companyName =
        item.companyRef?.companyName?.toLowerCase() ?? '';
      return (
        fullName.includes(lower) ||
        niNumber.includes(lower) ||
        utr.includes(lower) ||
        companyName.includes(lower)
      );
    });
  }, [earningData?.data, searchText]);

  const debouncedSearch = useCallback(
    debounce((value: string) => setSearchText(value), 500),
    [],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleExport = async (item: IEarningReportV2) => {
    setExportLoadingId(item.id);
    try {
      const res = await exportEarning({
        taxPeriod: item.taxPeriod,
        year: String(item.taxYear),
        companyId: item.companyRef?.id,
      });
      if (res?.success && res.data) {
        window.open(res.data, '_blank');
      }
    } finally {
      setExportLoadingId(null);
    }
  };

  const handleExportAll = async () => {
    if (!filteredList.length) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'] || 'No data to export',
      });
      return;
    }
    try {
      const res = await exportEarning({
        year: String(selectedTaxYearRef.current),
        taxPeriod: filterValues.taxPeriod,
      });
      if (res?.success && res.data) {
        window.open(res.data, '_blank');
      }
    } catch {
      // Error handled by hook
    }
  };

  const onChangeTable = useCallback(
    (sorter?: { field: string; order: 'desc' | 'asc' }) => {
      if (sorter?.order) {
        handleFetchData({
          year: String(selectedTaxYearRef.current),
          taxPeriod: filterValues.taxPeriod,
          sortField: sorter.field,
          sortType: sorter.order,
        });
      } else {
        handleFetchData({
          year: String(selectedTaxYearRef.current),
          taxPeriod: filterValues.taxPeriod,
          sortField: undefined,
          sortType: undefined,
        });
      }
    },
    [filterValues, handleFetchData],
  );

  const columns = getEarningStatementColumns({
    messages,
    payrollCountry,
    onExport: handleExport,
    exportLoadingId,
  });

  const renderTitle = useCallback(
    () => (
      <AppPageTitle
        prefix={messages['common.cisStatementOfEarnings'] || 'CIS Statement of Earnings'}
        separator=""
        title=""
      />
    ),
    [messages],
  );

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export'] || 'Export'}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportAll}
        />
      </div>
    );
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isExportLoading}
    >
      {isExportLoading && <AppLoader />}
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearchChange} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsFilterOpen(true)}
              />
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              <TaxYearSelect onFilter={handleTaxYearChange} />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={filteredList}
            loading={loading}
            page={0}
            total={filteredList.length}
            pageSize={filteredList.length || 20}
            scroll={{ x: 2000, y: 700 }}
            titleEmpty={
              messages['earningStatement.empty'] || 'No earning statements found.'
            }
            columns={columns}
            onChangeTable={onChangeTable}
            pagination={filteredList.length <= 20 ? false : undefined}
          />
        </AppsContent>
      </AppCard>
      <EarningStatementFilterModal
        isOpen={isFilterOpen}
        toggle={() => setIsFilterOpen(false)}
        onFilter={handleFilter}
      />
    </AppsContainer>
  );
};

export default EarningStatementListing;
